import { observer } from 'mobx-react-lite'
import { ChangeEvent, FormEvent, useState } from 'react'
import { CircularProgress } from '@mui/material'
import { AppBox } from 'src/component/common/analog/molecule/AppBox'
import { ModalWindow } from 'src/component/common/analog/template/ModalWindow'
import { useLocalizationStore } from 'src/feature/localization/store/localization.store'
import { AppTextField } from 'src/feature/form/component/analog/AppTextField'
import { AppForm } from 'src/component/common/analog/molecule/basic/AppForm'
import { AppButton } from 'src/component/common/analog/molecule/button/AppButton'
import { Caption } from 'src/feature/localization/component/atom/Caption'
import { useControlPanelStore } from 'src/feature/control-panel/store/control-panel.store'
import { OrganisationRole } from 'src/feature/control-panel/model/OrganisationRole'
import { AppDivider } from 'src/component/common/analog/molecule/AppDivider'
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined'
import { ColorTokens } from 'src/feature/theme/model/Palette'

export type AddUserModalProps = {
  show: boolean
  levelId: string
  onClose: (reload: boolean) => void
}

export const AddUser = observer(({ show, levelId, onClose }: AddUserModalProps) => {
  const { translation } = useLocalizationStore()
  const texts = translation.pages.controlpanelUsers.inviteDialog

  const controlPanelStore = useControlPanelStore()

  const [emails, setEmails] = useState('')
  const { isLoading } = controlPanelStore.getGenericFieldsState('inviteUser')

  const handleEmailChange = (event: ChangeEvent<HTMLInputElement>) => {
    setEmails(event.target.value)
  }

  const handleClose = (reload: boolean) => {
    onClose(reload)
  }

  const createInvite = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    await controlPanelStore.addUser(emails, [levelId], OrganisationRole.User)
    handleClose(true)
  }

  return (
    <ModalWindow
      modalProps={{
        open: show,
        onClose: () => handleClose(false),
        PaperProps: {
          sx: {
            maxWidth: { xs: '90%', md: '60%', lg: '30%' },
            borderRadius: '10px',
          },
        },
      }}
      titleProps={{
        textValue: texts.title,
      }}
      subtitleProps={{
        textValue: texts.subtitle,
        variant: 'body2',
        component: 'p',
        sx: styles.subtitle,
      }}
      headerIcon={<PersonAddAltOutlinedIcon sx={styles.headerIcon} />}
    >
      <AppBox sx={styles.container}>
        <AppForm sx={styles.form} onSubmit={createInvite}>
          <AppTextField
            label={texts.emailField}
            onChange={handleEmailChange}
            fullWidth
            required
            style={{ marginBottom: 0 }}
          />
          <Caption textValue={texts.emailHelp} style={styles.emailHelp} />
          <AppDivider />
          <AppBox sx={styles.actionsRow}>
            <AppButton
              textValue={translation.buttons.cancel}
              fontSize={12}
              variant="light"
              onClick={() => handleClose(false)}
            />
            <AppButton
              textValue={texts.sendButton}
              fontSize={12}
              variant="contained"
              disabled={isLoading}
              aria-label={texts.sendButton()}
              type='submit'
            >
              {isLoading ? <CircularProgress size='1em' color='inherit' /> : null}
            </AppButton>
          </AppBox>
        </AppForm>
      </AppBox>
    </ModalWindow>
  )
})

const styles = {
  container: {
    flexDirection: 'column',
    gap: 10,
  },
  emailHelp: {
    fontSize: 10,
  },
  subtitle: {
    color: 'text.secondary',
    lineHeight: 1.4,
  },
  headerIcon: {
    fontSize: 18,
    color: ColorTokens.primary400,
  },
  actionsRow: {
    gap: 10,
    justifyContent: 'flex-end',
    width: '100%',
  },
  form: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 10,
  },
}